import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useUI } from "@/context/UIContext";
import { Card, Badge, Btn, Input, Textarea, Select, Field, Modal } from "@/components/ui-kit";
import QRLabel from "@/components/QRLabel";
import { fmtMoney } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { Boxes, Plus, Minus, QrCode, AlertTriangle, Pencil } from "lucide-react";

const CATEGORIES = ["ICs", "Passive Components", "Power Modules", "Connectors", "Thermal", "Cables", "Other"];

export default function Inventory() {
  const { compact } = useUI();
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [lowOnly, setLowOnly] = useState(false);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const [qrFor, setQrFor] = useState(null);

  const load = useCallback(() => {
    const p = {};
    if (q) p.q = q;
    if (lowOnly) p.low_stock = true;
    api.get("/inventory", { params: p }).then((r) => setItems(r.data));
  }, [q, lowOnly]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const canEdit = can("inventory.manage");
  const adjust = async (item, delta) => {
    try { await api.post(`/inventory/${item.id}/adjust`, { delta, reason: delta > 0 ? "manual add" : "manual issue" }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const rowPad = compact ? "py-1.5 text-xs" : "py-2.5 text-sm";

  return (
    <div className="space-y-4" data-testid="inventory-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold uppercase tracking-tight sm:text-3xl">Inventory Control</h1>
          <p className="text-sm text-muted-foreground">Multi-bin component stock · reorder alerts · {items.length} SKUs</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search part / category…" className="w-48" data-testid="inv-search" />
          <button onClick={() => setLowOnly((v) => !v)} data-testid="low-stock-toggle"
            className={`flex h-9 items-center gap-1 rounded-md border px-3 text-xs font-semibold ${lowOnly ? "border-amber-500 bg-amber-500/15 text-amber-500" : "border-border"}`}>
            <AlertTriangle size={14} /> Low Stock
          </button>
          {canEdit && <Btn onClick={() => { setEdit(null); setOpen(true); }} data-testid="new-item-btn"><Plus size={16} /> Add Part</Btn>}
        </div>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full min-w-[860px]">
          <thead><tr className="border-b border-border text-left font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="px-3 py-2">Part #</th><th className="px-3 py-2">Name</th><th className="px-3 py-2">Category</th>
            <th className="px-3 py-2">Bin</th><th className="px-3 py-2 text-center">On Hand</th><th className="px-3 py-2">Unit Cost</th><th className="px-3 py-2">Supplier</th><th className="px-3 py-2 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {items.map((i) => {
              const low = i.quantity_on_hand <= i.min_reorder_level;
              return (
                <tr key={i.id} className={`border-b border-border/60 hover:bg-accent/50 ${rowPad}`} data-testid={`item-row-${i.id}`}>
                  <td className="px-3 font-mono text-xs text-primary">{i.part_number}</td>
                  <td className="px-3 font-medium">{i.name}</td>
                  <td className="px-3"><Badge className="border-border text-muted-foreground">{i.category || "—"}</Badge></td>
                  <td className="px-3 font-mono text-xs">{i.bin_location || "—"}</td>
                  <td className="px-3 text-center">
                    <span className={`font-mono font-bold ${low ? "text-amber-500" : ""}`}>{i.quantity_on_hand}</span>
                    <span className="font-mono text-[10px] text-muted-foreground"> /{i.min_reorder_level}</span>
                    {low && <AlertTriangle size={12} className="ml-1 inline text-amber-500" />}
                  </td>
                  <td className="px-3 font-mono text-xs">{fmtMoney(i.unit_cost)}</td>
                  <td className="px-3 text-xs">{i.supplier_name || "—"}</td>
                  <td className="px-3">
                    <div className="flex items-center justify-end gap-1">
                      {canEdit && <>
                        <button onClick={() => adjust(i, -1)} data-testid={`dec-${i.id}`} className="rounded border border-border p-1 hover:bg-accent"><Minus size={13} /></button>
                        <button onClick={() => adjust(i, 1)} data-testid={`inc-${i.id}`} className="rounded border border-border p-1 hover:bg-accent"><Plus size={13} /></button>
                        <button onClick={() => { setEdit(i); setOpen(true); }} data-testid={`edit-${i.id}`} className="rounded border border-border p-1 hover:bg-accent"><Pencil size={13} /></button>
                      </>}
                      <button onClick={() => setQrFor(i)} data-testid={`inv-qr-${i.id}`} className="rounded border border-border p-1 hover:bg-accent"><QrCode size={13} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={8} className="px-3 py-10 text-center text-muted-foreground">No inventory items.</td></tr>}
          </tbody>
        </table>
      </Card>

      {open && <ItemForm item={edit} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {qrFor && <QRLabel open onClose={() => setQrFor(null)} title={qrFor.name} code={qrFor.part_number} stamp={`Bin ${qrFor.bin_location || "—"}`}
        lines={[["CATEGORY", qrFor.category || "—"], ["BIN", qrFor.bin_location || "—"], ["ON HAND", String(qrFor.quantity_on_hand)], ["COST", fmtMoney(qrFor.unit_cost)]]} />}
    </div>
  );
}

function ItemForm({ item, onClose, onSaved }) {
  const [f, setF] = useState(item || { name: "", category: "ICs", description: "", unit_of_measure: "pcs", quantity_on_hand: 0, min_reorder_level: 5, bin_location: "", unit_cost: 0, supplier_name: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.name) { toast.error("Name required"); return; }
    const payload = { ...f, quantity_on_hand: Number(f.quantity_on_hand), min_reorder_level: Number(f.min_reorder_level), unit_cost: Number(f.unit_cost) };
    try {
      if (item) await api.put(`/inventory/${item.id}`, payload);
      else await api.post("/inventory", payload);
      toast.success(item ? "Updated" : "Part added"); onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open onClose={onClose} title={item ? "Edit Part" : "Add Part"} wide>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Name" required><Input data-testid="if-name" value={f.name} onChange={set("name")} /></Field>
        <Field label="Category"><Select value={f.category} onChange={set("category")}>{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</Select></Field>
        <Field label="Bin Location (Rack-Shelf-Bin)"><Input value={f.bin_location} onChange={set("bin_location")} placeholder="R1-S2-B04" /></Field>
        <Field label="Supplier"><Input value={f.supplier_name} onChange={set("supplier_name")} placeholder="Mouser / DigiKey / Local" /></Field>
        <Field label="Quantity On Hand"><Input type="number" value={f.quantity_on_hand} onChange={set("quantity_on_hand")} /></Field>
        <Field label="Min Reorder Level"><Input type="number" value={f.min_reorder_level} onChange={set("min_reorder_level")} /></Field>
        <Field label="Unit Cost ₹"><Input type="number" value={f.unit_cost} onChange={set("unit_cost")} /></Field>
        <Field label="Unit of Measure"><Input value={f.unit_of_measure} onChange={set("unit_of_measure")} /></Field>
      </div>
      <Field label="Description"><Textarea className="mt-3" rows={2} value={f.description} onChange={set("description")} /></Field>
      <div className="mt-4 flex justify-end gap-2"><Btn variant="outline" onClick={onClose}>Cancel</Btn><Btn onClick={save} data-testid="item-submit-btn">{item ? "Save" : "Add"}</Btn></div>
    </Modal>
  );
}
